/**
 * SendVendorCredentialsAction
 * ─────────────────────────────────────────────────────────────────────────────
 * The "Send Login Details" row action shared by every vendor directory
 * (Restaurants, R&W, Healthcare, Brands).
 *
 * WHAT IT SENDS
 * -------------
 * The vendor's LOGIN EMAIL and a single-use, short-lived set-password link.
 * Never a password: passwords are stored as bcrypt hashes, so there is no
 * readable one to send, and minting a new one to read out over the phone would
 * lock the vendor out of their current session and leave a permanent credential
 * sitting in an inbox. See services/auth/vendorCredentialDelivery.js.
 *
 * WHY THE DIALOG ASKS THE SERVER BEFORE IT ASKS THE ADMIN
 * -------------------------------------------------------
 * The directory row shows the vendor's BUSINESS email. The address that can
 * actually sign in is on the linked owner account, and the two are frequently
 * different (the business address is the one printed on the shopfront; the
 * login is whoever set the account up). Confirming against the row would let an
 * admin approve a send to an address the mail was never going to reach — the
 * exact mistake the confirmation step exists to prevent. So opening the dialog
 * does a preflight and shows the address the server resolved, or the reason
 * there isn't one.
 *
 * RENDERING
 * ---------
 * The trigger is supplied by the caller as a function child, so each directory
 * keeps its own button styling (`rst-btn`, `rw-btn`, `PbActionBtn`,
 * `bm-action-btn`, …) instead of this component imposing a fifth look:
 *
 *   <SendVendorCredentialsAction vendorType="RESTAURANT" vendorId={r._id}
 *                                vendorName={r.name} visible={isSuperAdmin}>
 *     {({ open, busy }) => (
 *       <button className="rst-btn" onClick={open} disabled={busy}>Send Login</button>
 *     )}
 *   </SendVendorCredentialsAction>
 *
 * `visible` is the caller's super-admin predicate. When it is false the whole
 * thing renders nothing — and the server rejects the call anyway, so hiding the
 * button is presentation, not the security boundary.
 *
 * WHEN MAIL IS DOWN: SET LOGIN
 * ----------------------------
 * The same dialog offers "Set a password instead" (SetVendorLoginDialog): the
 * admin sets the login email and a password and sends them to the vendor
 * themselves. It is offered quietly while mail works, and pushed forward when
 * it cannot help: the vendor has no email on file, or the send just failed at
 * the mail provider. The render prop also exposes `openSetLogin` for a
 * directory that wants its own button for it.
 */

import React, { useCallback, useState } from "react";
import { Modal, Spinner } from "react-bootstrap";
import { FaKey, FaExclamationTriangle, FaPaperPlane, FaHistory } from "react-icons/fa";
import { toast } from "react-toastify";

import {
  apiGetVendorCredentialTarget,
  apiSendVendorCredentials,
} from "../../api/index";
import SetVendorLoginDialog from "./SetVendorLoginDialog";

/**
 * Module scope on purpose: a helper defined inside the component body would be
 * a new function identity on every render and would have to be listed in (or
 * excluded from) every hook's dependency array. Hoisting anything that closes
 * over nothing keeps the hooks below genuinely dependency-clean.
 */
const formatWhen = (value) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleString(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

/** Pull the most useful sentence out of an axios failure. */
const errorMessage = (err, fallback) =>
  err?.response?.data?.message || err?.message || fallback;

/**
 * Refusals that "Set login" can get round: the mail could not be sent, or
 * there is no address to send it to. Everything else (no owner account, a
 * staff account, a deactivated login) blocks both paths, so offering it there
 * would only lead to a second refusal.
 */
const MAIL_FAILURE = /^(MAIL_|EMAIL_SEND_FAILED$)/;
const SET_LOGIN_FIXES = (code) => code === "VENDOR_HAS_NO_EMAIL" || MAIL_FAILURE.test(String(code || ""));

const linkButtonStyle = {
  background: "none",
  border: "none",
  padding: 0,
  color: "#083860",
  fontWeight: 700,
  textDecoration: "underline",
  cursor: "pointer",
  fontSize: "inherit",
};

const SendVendorCredentialsAction = ({
  vendorType,
  vendorId,
  vendorName,
  visible = false,
  children,
}) => {
  const [showModal, setShowModal] = useState(false);
  const [loadingTarget, setLoadingTarget] = useState(false);
  // Resolved destination + link lifetime, or null while unknown.
  const [target, setTarget] = useState(null);
  // A refusal from the server: { code, message }. Kept separate from `target`
  // so the dialog can render an explanation INSTEAD of a send button.
  const [blocked, setBlocked] = useState(null);
  const [lastSend, setLastSend] = useState(null);
  const [sending, setSending] = useState(false);
  // The provider refused the send just now: { code, message }.
  const [mailFailure, setMailFailure] = useState(null);
  const [setLoginOpen, setSetLoginOpen] = useState(false);

  const openDialog = useCallback(async () => {
    setShowModal(true);
    setLoadingTarget(true);
    setTarget(null);
    setBlocked(null);
    setMailFailure(null);
    try {
      const { data } = await apiGetVendorCredentialTarget(vendorType, vendorId);
      setTarget(data);
      setLastSend(data?.lastSend || null);
    } catch (err) {
      const payload = err?.response?.data;
      setBlocked({
        code: payload?.code || "PREFLIGHT_FAILED",
        message: errorMessage(
          err,
          "Could not work out where to send this vendor's sign-in details."
        ),
      });
      setLastSend(payload?.lastSend || null);
    } finally {
      setLoadingTarget(false);
    }
  }, [vendorType, vendorId]);

  const closeDialog = useCallback(() => {
    if (sending) return; // don't leave a send in flight with no way to see it land
    setShowModal(false);
  }, [sending]);

  const confirmSend = useCallback(async () => {
    setSending(true);
    setMailFailure(null);
    try {
      const { data } = await apiSendVendorCredentials(vendorType, vendorId);
      // Report what the server says happened, not what we hoped would happen.
      toast.success(data?.message || `Sign-in details sent to ${data?.sentTo}.`);
      setLastSend({ at: data?.sentAt || new Date().toISOString(), to: data?.sentTo });
      setShowModal(false);
    } catch (err) {
      const message = errorMessage(err, "Could not send the sign-in details.");
      const code = err?.response?.data?.code;
      toast.error(message);
      // Keep the dialog open on a mail failure and offer the way round it,
      // rather than leaving the admin with a toast and nothing to do next.
      if (MAIL_FAILURE.test(String(code || ""))) setMailFailure({ code, message });
    } finally {
      setSending(false);
    }
  }, [vendorType, vendorId]);

  const openSetLogin = useCallback(() => {
    if (sending) return;
    setShowModal(false);
    setSetLoginOpen(true);
  }, [sending]);

  const closeSetLogin = useCallback(() => setSetLoginOpen(false), []);

  if (!visible) return null;

  const lastSentLabel = formatWhen(lastSend?.at);
  const displayName = target?.vendorName || vendorName || "this vendor";

  return (
    <>
      {children({ open: openDialog, openSetLogin, busy: sending, lastSentLabel })}

      <SetVendorLoginDialog
        open={setLoginOpen}
        onClose={closeSetLogin}
        vendorType={vendorType}
        vendorId={vendorId}
        vendorName={target?.vendorName || vendorName}
      />

      <Modal show={showModal} onHide={closeDialog} centered>
        {/* Light header like every other dialog (assets/css/lusuku-overrides.css
            "One family"). The old inline navy background survived the sweep
            that made all modal titles dark, which left this title dark on
            navy and unreadable. */}
        <Modal.Header closeButton>
          <Modal.Title style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <FaKey style={{ color: "#083860" }} />
            Send Login Details: {displayName}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body style={{ padding: "20px 24px" }}>
          {loadingTarget && (
            <div style={{ textAlign: "center", padding: "24px 0" }}>
              <Spinner animation="border" variant="success" size="sm" />
              <p style={{ marginTop: 10, color: "#9aacbf", fontSize: "0.85rem" }}>
                Checking where this vendor can be reached…
              </p>
            </div>
          )}

          {!loadingTarget && blocked && (
            <div
              style={{
                background: "#fff3f4",
                border: "1.5px solid #f5c2c7",
                borderRadius: 8,
                padding: "12px 16px",
                color: "#c62828",
                fontSize: "0.85rem",
                display: "flex",
                gap: 10,
                alignItems: "flex-start",
              }}
            >
              <FaExclamationTriangle style={{ marginTop: 2, flexShrink: 0 }} />
              <span>{blocked.message}</span>
            </div>
          )}

          {!loadingTarget && blocked && SET_LOGIN_FIXES(blocked.code) && (
            <p style={{ fontSize: "0.82rem", color: "#4a5568", marginTop: 12, marginBottom: 0 }}>
              You can set their login email and a password here instead, and send the details to them
              yourself on WhatsApp.
            </p>
          )}

          {!loadingTarget && !blocked && target && (
            <>
              <p style={{ fontSize: "0.88rem", marginBottom: 14, color: "#4a5568" }}>
                Lusuku will email <strong>{displayName}</strong> their login
                address and a link to set their own password.
              </p>

              <div
                style={{
                  background: "#f4f6f9",
                  border: "1.5px solid #e4e8ef",
                  borderRadius: 8,
                  padding: "12px 16px",
                  marginBottom: 14,
                }}
              >
                <div
                  style={{
                    fontSize: "0.68rem",
                    fontWeight: 700,
                    letterSpacing: "0.08em",
                    textTransform: "uppercase",
                    color: "#8a9ab0",
                    marginBottom: 4,
                  }}
                >
                  Will be sent to
                </div>
                <div
                  style={{
                    fontSize: "0.95rem",
                    fontWeight: 700,
                    color: "#1a2a3a",
                    wordBreak: "break-all",
                  }}
                >
                  {target.loginEmail}
                </div>
                <div style={{ fontSize: "0.72rem", color: "#8a9ab0", marginTop: 4 }}>
                  This is the vendor's login address. Check it is still current
                  before sending.
                </div>
              </div>

              <div
                style={{
                  background: "#e8f5e9",
                  border: "1px solid #c8e6c9",
                  borderRadius: 8,
                  padding: "10px 14px",
                  fontSize: "0.78rem",
                  color: "#2e7d32",
                }}
              >
                No password is included. The link can be used <strong>once</strong>{" "}
                and expires in{" "}
                <strong>
                  {target.linkTtlHours} hour
                  {Number(target.linkTtlHours) === 1 ? "" : "s"}
                </strong>
                . Sending also cancels any earlier link for this account.
              </div>

              {lastSentLabel && (
                <div
                  style={{
                    marginTop: 12,
                    fontSize: "0.76rem",
                    color: "#8a9ab0",
                    display: "flex",
                    gap: 6,
                    alignItems: "center",
                  }}
                >
                  <FaHistory style={{ fontSize: 10 }} />
                  Last sent {lastSentLabel}
                  {lastSend?.to ? ` to ${lastSend.to}` : ""}
                  {lastSend?.byName ? ` by ${lastSend.byName}` : ""}.
                </div>
              )}

              {mailFailure ? (
                <div
                  role="alert"
                  style={{
                    marginTop: 14,
                    background: "#fff3f4",
                    border: "1.5px solid #f5c2c7",
                    borderRadius: 8,
                    padding: "10px 14px",
                    fontSize: "0.8rem",
                    color: "#8f1d20",
                  }}
                >
                  <div style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
                    <FaExclamationTriangle style={{ marginTop: 2, flexShrink: 0 }} />
                    <span>{mailFailure.message}</span>
                  </div>
                  <div style={{ marginTop: 8, color: "#4a5568" }}>
                    Email is not getting through.{" "}
                    <button type="button" onClick={openSetLogin} style={linkButtonStyle}>
                      Set a password and send it yourself
                    </button>
                  </div>
                </div>
              ) : (
                <div style={{ marginTop: 14, fontSize: "0.76rem", color: "#6b7c93" }}>
                  Email not getting through?{" "}
                  <button type="button" onClick={openSetLogin} disabled={sending} style={linkButtonStyle}>
                    Set a password and send it yourself
                  </button>
                </div>
              )}
            </>
          )}
        </Modal.Body>

        <Modal.Footer style={{ borderTop: "1px solid #f0f2f5", gap: 8 }}>
          <button
            type="button"
            onClick={closeDialog}
            disabled={sending}
            style={{
              height: 36,
              padding: "0 20px",
              borderRadius: 8,
              border: "1.5px solid #e4e8ef",
              background: "#f4f6f9",
              color: "#6b7c93",
              fontSize: "0.82rem",
              fontWeight: 700,
              cursor: sending ? "not-allowed" : "pointer",
            }}
          >
            {blocked ? "Close" : "Cancel"}
          </button>
          {blocked && SET_LOGIN_FIXES(blocked.code) && (
            <button
              type="button"
              onClick={openSetLogin}
              style={{
                height: 36,
                padding: "0 20px",
                borderRadius: 8,
                border: "none",
                background: "#023957",
                color: "#fff",
                fontSize: "0.82rem",
                fontWeight: 700,
                display: "inline-flex",
                alignItems: "center",
                gap: 7,
                cursor: "pointer",
              }}
            >
              <FaKey style={{ fontSize: 11 }} />
              Set login email and password
            </button>
          )}
          {!blocked && (
            <button
              type="button"
              onClick={confirmSend}
              disabled={loadingTarget || sending || !target}
              style={{
                height: 36,
                padding: "0 20px",
                borderRadius: 8,
                border: "none",
                background: "#023957",
                color: "#fff",
                fontSize: "0.82rem",
                fontWeight: 700,
                display: "inline-flex",
                alignItems: "center",
                gap: 7,
                cursor:
                  loadingTarget || sending || !target ? "not-allowed" : "pointer",
                opacity: loadingTarget || sending || !target ? 0.6 : 1,
              }}
            >
              {sending ? (
                <>
                  <Spinner as="span" animation="border" size="sm" />
                  Sending…
                </>
              ) : (
                <>
                  <FaPaperPlane style={{ fontSize: 11 }} />
                  Send Login Details
                </>
              )}
            </button>
          )}
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default SendVendorCredentialsAction;
